/**
 * wellnessApi.js — API functions for the Wellness Report page.
 *
 * fetchWellnessReport       — runs the wellness report query
 * fetchWellnessFilterOptions — loads filter option sets (areas, residents, devices)
 */

import { postReport } from '../../../../services/api.js';
import { reportFilterFields } from '../../../../utils/reportRequestBody.js';
import { fetchPortalFilters, fetchWindowOptions } from '../../../../utils/reportFilterOptions.js';


// ---------------------------------------------------------------------------
// Fetch Wellness Report
// ---------------------------------------------------------------------------

/**
 * Runs the wellness report query.
 *
 * @param {object} opts
 * @param {number} opts.campusId
 * @param {string} opts.username
 * @param {string} opts.startDate   — ISO format (yyyy-mm-dd hh:mm:ss)
 * @param {string} opts.endDate     — ISO format
 * @param {string} opts.reportType  — 'check-in' | 'missed-check-in' | 'check-in-history' | 'rounds' | 'rounds-history'
 * @param {object} opts.filters     — filter selections (CSV strings; `windows` only for the
 *                                   reports with a windows filter)
 * @param {number} [opts.tzOffset]  — campus offset; the Lambda uses it to leave out windows that
 *                                   haven't started and mark one in progress
 *                                   (docs/CHECKIN_WINDOW_FILTER_PLAN.md)
 * @returns {Promise<object>} — { records, summary, overlimit, url, ErrorCode }
 */
export async function fetchWellnessReport({ campusId, username, startDate, endDate, reportType, filters = {}, tzOffset }) {
    const body = {
        rep_type: reportType,
        username: username || '',
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        event_category_filter: '1',
        tz_offset: Number(tzOffset) || 0,
        ...reportFilterFields(filters, { omit: ['device_filter', 'reason_filter', 'user_filter', 'event_type_filter', 'user_group_filter'] }),
    };

    const res = await postReport(body, '[wellnessApi] fetchWellnessReport error');
    if (res.ErrorCode) return { ErrorCode: res.ErrorCode };
    const data = res.data;

    return {
        records: data?.records || [],
        summary: data?.summary || [],
        overlimit: Boolean(data?.overlimit),
        url: data?.url || '',
    };
}

// ---------------------------------------------------------------------------
// Fetch Filter Options
// ---------------------------------------------------------------------------

/** Wellness's option subset (no user groups, events or devices), plus the check-in and
    rounds windows for the windows filter. */
export async function fetchWellnessFilterOptions(campusId) {
    try {
        const [portal, windowOptions] = await Promise.all([
            fetchPortalFilters(campusId,
                ['areas', 'areaGroups', 'areaTypes', 'residents', 'residentGroups', 'deviceCategories', 'users']),
            fetchWindowOptions(campusId),
        ]);
        return {
            ...windowOptions,
            areas: portal.areas || [],
            areaGroups: portal.areaGroups || [],
            areaTypes: portal.areaTypes || [],
            residents: portal.residents || [],
            residentGroups: portal.residentGroups || [],
            deviceCategories: portal.deviceCategories || [],
            users: portal.users || [],
        };
    } catch (err) {
        console.error('[wellnessApi] fetchWellnessFilterOptions error:', err);
        return {};
    }
}
